import React, { useMemo, useState, useEffect } from 'react';
import { fetchWithAuth, approveOrgRequests } from '../Services/api';
import ApproveFormModal from '../Components/ApproveFormModal';
import DeviceInfoCell from '../Components/DeviceInfoCell';
import MessageModal from '../Components/MessageModal';
import { getDeviceHostname, getRequestContactColumns } from '../utils/deviceInfo';
import './LicenseRequestsPage.css';

const LicenseRequestsPage = ({ showAlert, showConfirm }) => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showApproveForm, setShowApproveForm] = useState(null);
  const [modalMessage, setModalMessage] = useState('');

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/license-requests');
      setRequests(response.data || []);
      setFilteredRequests(response.data || []);
    } catch (err) {
      console.error('Error loading requests:', err);
      showAlert('Failed to load license requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredRequests(requests);
    } else {
      setFilteredRequests(requests.filter(r => r.status === activeFilter));
    }
  }, [activeFilter, requests]);

  const handleApprove = async (requestId, formData) => {
    try {
      const response = await fetchWithAuth(`/license-requests/${requestId}/approve`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      const approvedRequest = requests.find(r => r.id === requestId);
      const licenseKey = response.data?.licenseKey || response.data?.license?.license_key || 'N/A';

      setShowApproveForm(null);
      setModalMessage(
        `License approved and generated successfully!\n\n` +
        `Client: ${approvedRequest?.client_name || 'Unknown Client'}\n` +
        `Email: ${approvedRequest?.email || 'N/A'}\n` +
        `MAC Address: ${approvedRequest?.device_id || 'N/A'}\n` +
        `License Key: ${licenseKey}\n\n` +
        `The license has been Approved and is ready to use.`
      );
      showAlert('License approved and generated successfully!', 'success');
      loadRequests();
    } catch (err) {
      console.error('Error approving request:', err);
      showAlert(`Failed to approve request: ${err.message}`, 'error');
    }
  };

  const handleReject = async (requestId) => {
    showConfirm(
      'Enter reason for rejection:',
      async (reason) => {
        if (!reason || reason.trim() === '') {
          showAlert('Rejection reason is required', 'warning');
          return;
        }
        try {
          await fetchWithAuth(`/license-requests/${requestId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ reason })
          });
          showAlert('Request rejected successfully', 'success');
          loadRequests();
        } catch (err) {
          console.error('Error rejecting request:', err);
          showAlert('Failed to reject request', 'error');
        }
      },
      'reject'
    );
  };

  const handleDelete = async (requestId) => {
    showConfirm(
      'Are you sure you want to delete this request? This action cannot be undone.',
      async () => {
        try {
          await fetchWithAuth(`/license-requests/${requestId}`, {
            method: 'DELETE'
          });
          showAlert('Request deleted successfully', 'success');
          loadRequests();
        } catch (err) {
          console.error('Error deleting request:', err);
          showAlert('Failed to delete request', 'error');
        }
      },
      'delete'
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      default: return 'status-default';
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  const orgGroups = useMemo(() => {
    const pendingOrg = requests.filter(
      (r) => r.status === 'pending' && r.customer_type === 'organisation' && r.org_name
    );
    const grouped = {};
    pendingOrg.forEach((r) => {
      const key = r.org_name.trim();
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(r);
    });
    return grouped;
  }, [requests]);

  const handleApproveOrg = async (orgName) => {
    try {
      const response = await approveOrgRequests({ orgName, expiryDays: 365 });
      const approved = response.approved || [];
      setModalMessage(
        `Approved ${approved.length} system(s) for ${orgName}.\n\n` +
        approved.map((a) => `${a.systemName}: ${a.licenseKey}`).join('\n')
      );
      showAlert('Organisation requests approved', 'success');
      loadRequests();
    } catch (err) {
      showAlert(`Failed: ${err.message}`, 'error');
    }
  };

  return (
    <div className="requests-page">
      <div className="requests-stats">
        {[
          { label: 'Total Requests', value: stats.total, color: 'stat-total' },
          { label: 'Pending', value: stats.pending, color: 'stat-pending' },
          { label: 'Approved', value: stats.approved, color: 'stat-approved' },
          { label: 'Rejected', value: stats.rejected, color: 'stat-rejected' }
        ].map((stat, idx) => (
          <div key={idx} className="stat-card">
            <div className="stat-label">{stat.label}</div>
            <div className={`stat-value ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="requests-filters">
        <div className="filter-buttons">
          <span className="filter-label">Filter:</span>
          {['all', 'pending', 'approved', 'rejected'].map(filter => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
            >
              {filter}
            </button>
          ))}
          <button onClick={loadRequests} className="refresh-btn">
            🔄 Refresh
          </button>
        </div>
      </div>

      {Object.keys(orgGroups).length > 0 && (
        <div className="org-groups-panel">
          <h3>Organisation request groups</h3>
          {Object.entries(orgGroups).map(([orgName, group]) => (
            <div key={orgName} className="org-group-card">
              <div>
                <strong>{orgName}</strong>
                <div className="org-group-meta">{group.length} pending system(s)</div>
                <ul className="org-group-systems">
                  {group.map((r) => (
                    <li key={r.id}>
                      {r.system_name || r.client_name} — {getDeviceHostname(r.device_info)}
                    </li>
                  ))}
                </ul>
              </div>
              <button type="button" className="btn-approve" onClick={() => handleApproveOrg(orgName)}>
                Approve all for this org
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="requests-table-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p className="empty-title">No requests found</p>
            <small className="empty-subtitle">
              {activeFilter === 'all' ? 'No license requests yet' : `No ${activeFilter} requests`}
            </small>
          </div>
        ) : (
          <div className="requests-table-scroll">
          <table className="requests-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Type</th>
                <th>Org / System</th>
                <th>Clinic</th>
                <th>Phone</th>
                <th>City</th>
                <th>Device ID</th>
                <th>Device Info</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => {
                const contactCols = getRequestContactColumns(request);
                return (
                <tr key={request.id}>
                  <td className="client-cell">
                    <div className="client-name">{request.client_name}</div>
                    {request.email && (
                      <div className="client-email" title={request.email}>
                        {request.email}
                      </div>
                    )}
                  </td>
                  <td>{request.customer_type || 'individual'}</td>
                  <td>
                    {request.customer_type === 'organisation'
                      ? `${request.org_name || '-'} / ${request.system_name || '-'}`
                      : '-'}
                  </td>
                  <td className="clinic-cell">{contactCols.clinic}</td>
                  <td className="phone-cell">{contactCols.phone}</td>
                  <td className="city-cell">{contactCols.city}</td>
                  <td className="device-id-cell">
                    <code className="device-id-code" title={request.device_id}>
                      {request.device_id}
                    </code>
                  </td>
                  <td className="device-info-cell">
                    <DeviceInfoCell deviceInfo={request.device_info} />
                  </td>
                  <td className="status-cell">
                    <span className={`request-status ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="request-date">
                    {new Date(request.created_at).toLocaleString()}
                  </td>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => setShowApproveForm(request)}
                            className="btn-approve"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(request.id)}
                            className="btn-reject"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {request.status !== 'pending' && (
                        <button
                          onClick={() => handleDelete(request.id)}
                          className="btn-delete"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showApproveForm && (
        <ApproveFormModal
          request={showApproveForm}
          onApprove={(formData) => handleApprove(showApproveForm.id, formData)}
          onCancel={() => setShowApproveForm(null)}
          showAlert={showAlert}
        />
      )}

      {modalMessage && (
        <MessageModal
          message={modalMessage}
          onClose={() => setModalMessage('')}
        />
      )}
    </div>
  );
};

export default LicenseRequestsPage;